import { backendApi } from "./backendApi.rtkq";

export interface AdminInvitation {
  id: string;
  email: string;
  officeId?: string;
  officeName?: string;
  role: string;
  status: string;
  token: string;
  acceptUrl: string;
  expiresAt: string;
  createdAt: string;
}

export interface InvitationDetails {
  id: string;
  email: string;
  role: string;
  officeId?: string;
  officeName?: string;
  isSystemInvite: boolean;
  expiresAt: string;
}

export interface AcceptResult {
  accepted: boolean;
  officeId: string;
  officeName: string;
  role: string;
}

export interface CreatedOffice {
  id: string;
  name: string;
  slug: string;
}

export interface InvitationSummary {
  id: string;
  email: string;
  token: string;
  status: string;
  expiresAt: string;
  createdAt: string;
}

export interface BasicUser {
  id: string;
  email: string;
  displayName?: string;
}

export type CreateInvitationResult =
  | {
      type: "invitation_created";
      office: CreatedOffice;
      invitation: InvitationSummary;
      acceptUrl: string;
    }
  | {
      type: "office_assigned";
      office: CreatedOffice;
      user: BasicUser;
    }
  | {
      type: "already_exists_pending";
      office: CreatedOffice;
      invitation: InvitationSummary;
      acceptUrl: string;
    }
  | {
      type: "already_exists_assigned";
      office: CreatedOffice;
      user: BasicUser;
    };

export const adminInvitationsApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminInvitations: builder.query<AdminInvitation[], void>({
      query: () => "/admin/invitations",
      providesTags: ["Invitations"],
    }),

    createAdminInvitation: builder.mutation<
      CreateInvitationResult,
      { email: string; createOffice?: boolean; officeName?: string; planId?: string }
    >({
      query: (body) => ({ url: "/admin/invitations", method: "POST", body }),
      invalidatesTags: ["Invitations", "Accounts"],
    }),

    cancelAdminInvitation: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/admin/invitations/${id}`, method: "DELETE" }),
      invalidatesTags: ["Invitations"],
    }),

    getInvitationByToken: builder.query<InvitationDetails, string>({
      query: (token) => `/invitations/${token}`,
    }),

    acceptInvitation: builder.mutation<AcceptResult, string>({
      query: (token) => ({ url: `/invitations/${token}/accept`, method: "POST" }),
      invalidatesTags: ["Invitations", "Accounts"],
    }),
  }),
});

export const {
  useGetAdminInvitationsQuery,
  useCreateAdminInvitationMutation,
  useCancelAdminInvitationMutation,
  useGetInvitationByTokenQuery,
  useAcceptInvitationMutation,
} = adminInvitationsApi;
